/**
 * 15-oct-2026 — de qué EQUIPO sale cada pedido, y el aviso cuando otro equipo cerró la sesión.
 *
 * El navegador guarda un identificador al azar la primera vez y lo manda en cada pedido con la cabecera
 * `X-SIL-Equipo` (`api.js`). No identifica a la persona ni a la computadora: identifica a ESTE navegador en esta
 * computadora. Con eso Seguridad → Cuentas dice desde cuántos equipos se usa cada cuenta y cuántos a la vez
 * (`backend/equipos.py`). Si el navegador no deja guardar, el identificador vive mientras la página esté abierta.
 *
 * Con el interruptor «una sesión abierta por cuenta», entrar desde otro equipo cierra la sesión de este: el servidor
 * contesta 401 con `codigo: "sesion_reemplazada"`, `api.js` guarda el aviso y la pantalla de ingreso lo muestra.
 */
export const CABECERA_EQUIPO = "X-SIL-Equipo";
export const CLAVE_EQUIPO = "qb_equipo";
export const CLAVE_AVISO_INGRESO = "qb_aviso_ingreso";
export const CODIGO_SESION_REEMPLAZADA = "sesion_reemplazada";
const VALIDO = /^[A-Za-z0-9_-]{4,40}$/;
const LETRAS = "abcdefghijklmnopqrstuvwxyz0123456789";

let enMemoria = null;

/** Un identificador nuevo de 16 caracteres. `azar(n)` devuelve n números de 0 a 255 (inyectable para los tests). */
export function generarId(azar = azarDelNavegador) {
  return Array.from(azar(16), (b) => LETRAS[b % LETRAS.length]).join("");
}

function azarDelNavegador(n) {
  const a = new Uint8Array(n);
  try {
    window.crypto.getRandomValues(a);
  } catch {
    for (let i = 0; i < n; i += 1) a[i] = Math.floor(Math.random() * 256);
  }
  return a;
}

/** El identificador de este navegador: el guardado o uno nuevo. Nunca levanta. */
export function idEquipo(almacen = typeof window !== "undefined" ? window.localStorage : null, azar) {
  try {
    const guardado = almacen?.getItem(CLAVE_EQUIPO);
    if (guardado && VALIDO.test(guardado)) return guardado;
    const nuevo = generarId(azar);
    almacen.setItem(CLAVE_EQUIPO, nuevo);
    return nuevo;
  } catch {
    if (!enMemoria) enMemoria = `t${generarId(azar)}`.slice(0, 16);
    return enMemoria;
  }
}

/** PURA. ¿Este error es «otro equipo entró con tu usuario»? Devuelve el mensaje del servidor, o null. */
export function avisoDeSesionReemplazada(err) {
  const r = err?.response;
  if (r?.status !== 401) return null;
  const d = r?.data?.detail;
  return d && typeof d === "object" && d.codigo === CODIGO_SESION_REEMPLAZADA ? (d.mensaje || "") : null;
}

/** Guarda el aviso para la pantalla de ingreso (sobrevive al cambio de página). Nunca levanta. */
export function guardarAvisoIngreso(mensaje, almacen = typeof window !== "undefined" ? window.sessionStorage : null) {
  try { if (mensaje) almacen.setItem(CLAVE_AVISO_INGRESO, mensaje); } catch { /* sin storage: no hay aviso */ }
}

/** Lee el aviso guardado SIN borrarlo: la pantalla puede armarse más de una vez antes de mostrarse. */
export function leerAvisoIngreso(almacen = typeof window !== "undefined" ? window.sessionStorage : null) {
  try {
    return almacen.getItem(CLAVE_AVISO_INGRESO) || null;
  } catch {
    return null;
  }
}

/** Borra el aviso (la pantalla lo llama después de mostrarlo: se ve una vez). */
export function borrarAvisoIngreso(almacen = typeof window !== "undefined" ? window.sessionStorage : null) {
  try { almacen.removeItem(CLAVE_AVISO_INGRESO); } catch { /* sin storage */ }
}
